import React from 'react';
import { Link } from 'react-router-dom';

export default function AffiliateTerms() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pt-24 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <Link to="/affiliate" className="text-cyan-400 text-sm hover:underline">&larr; Back to Affiliate</Link>
        <h1 className="text-3xl md:text-4xl font-bold mt-4 mb-2">Affiliate Program Terms</h1>
        <p className="text-sm text-gray-500 mb-10">Last updated: June 9, 2026</p>

        <div className="prose prose-invert max-w-none space-y-6 text-gray-300 text-sm leading-relaxed">
          <Section title="1. The program in one sentence">
            <p>
              SnapChart AI&apos;s Affiliate Program pays you a recurring <strong>30%</strong> commission on every SaaS subscription that a user activates through your unique referral link. Commissions accrue on each paid invoice (Stripe-confirmed) and are settled <strong>monthly</strong> by USDT or PayPal.
            </p>
          </Section>

          <Section title="2. What you can promote">
            <ul>
              <li>SnapChart AI as a <strong>software product</strong>: chart-grading, AI Risk Officer, NO-TRADE intelligence, counterfactual capital tracking, Trader Discipline Index (TDI).</li>
              <li>The free 30-day trial.</li>
              <li>The institutional discipline & risk-management positioning.</li>
            </ul>
          </Section>

          <Section title="3. What you MUST NOT do">
            <p className="text-amber-300 font-medium">This is the most important section. Read it carefully.</p>
            <ul>
              <li>
                <strong>No financial-performance claims or guarantees about trading outcomes.</strong> You cannot say, imply, or share screenshots suggesting that SnapChart AI will produce a specific profit, win rate, return, or financial result. The product helps with risk discipline; it does not promise gains.
              </li>
              <li>No fabricated testimonials. Do not claim other people&apos;s wins as ours.</li>
              <li>No paid ads on protected brand terms (&quot;SnapChart AI&quot;, &quot;Snap Chart AI&quot;, etc.) without prior written permission.</li>
              <li>No self-referral. Referring your own additional accounts is fraud and forfeits all commissions.</li>
              <li>No bots, cookie stuffing, click-fraud, or other artificial-traffic schemes.</li>
              <li>No incentivized referrals where you offer the referred user cash/crypto/rewards in exchange for signing up.</li>
              <li>No spam (mass DMs, scraped email lists, hijacked accounts).</li>
            </ul>
            <p>Violations result in immediate suspension and forfeiture of all accrued, unpaid commissions.</p>
          </Section>

          <Section title="4. Commission structure">
            <ul>
              <li><strong>30%</strong> of the gross USD amount of every successful Stripe invoice paid by users you referred.</li>
              <li>Applies <strong>recurring</strong> — every renewal counts, with no expiry, for as long as the referred user keeps paying.</li>
              <li>Applies on monthly and annual subscriptions identically.</li>
              <li>Does <strong>not</strong> apply on refunds, chargebacks, taxes, Stripe fees, or comped accounts.</li>
              <li>A refunded or charged-back invoice will <strong>reverse</strong> the corresponding commission.</li>
            </ul>
          </Section>

          <Section title="5. Payouts">
            <ul>
              <li>Payouts are issued <strong>monthly</strong>, in arrears, by the 10th of the following month.</li>
              <li>Minimum payout threshold: <strong>$20 USD</strong>. Balances below this roll over to the next month.</li>
              <li>Methods: <strong>USDT</strong> (TRC-20 or ERC-20) <strong>or PayPal</strong>. You choose at activation; you may switch at any time before a month is closed.</li>
              <li>You are responsible for providing a valid wallet address or PayPal email. Lost funds due to incorrect destination cannot be recovered.</li>
              <li>You are responsible for any income, tax, or reporting obligations on commissions earned in your jurisdiction.</li>
            </ul>
          </Section>

          <Section title="6. Attribution rules">
            <ul>
              <li>A user is attributed to you when they sign up after clicking your <code>?ref=&lt;your_code&gt;</code> link, within a <strong>30-day attribution window</strong>.</li>
              <li>Attribution is <strong>last-touch</strong>: the most recent matching ref click wins.</li>
              <li>Once a user is attributed to an affiliate, the attribution is <strong>permanent</strong> — it does not transfer to another affiliate later.</li>
              <li>Self-referrals are detected and rejected automatically.</li>
            </ul>
          </Section>

          <Section title="7. Termination">
            <p>
              You may leave the program at any time by emailing <a className="text-cyan-400" href="mailto:support@snap-chart.ai">support@snap-chart.ai</a>. SnapChart AI may suspend or terminate any affiliate at our sole discretion for violations of these terms. On termination, accrued but unpaid commissions earned in compliance with these terms will be paid out in the next regular cycle.
            </p>
          </Section>

          <Section title="8. Changes to these terms">
            <p>
              We may update these terms. We&apos;ll post the new version here with an updated &quot;Last updated&quot; date. Material changes will be emailed to active affiliates. Continued participation after a change constitutes acceptance.
            </p>
          </Section>

          <Section title="9. Not financial advice">
            <p>
              SnapChart AI is a software tool. It is not a broker, investment adviser, or asset manager. Nothing on snap-chart.ai, in our app, or in any communication should be interpreted as personalized financial advice. Trading involves risk of loss. Past performance does not guarantee future results.
            </p>
          </Section>

          <Section title="10. Contact">
            <p>
              Questions about the affiliate program: <a className="text-cyan-400" href="mailto:support@snap-chart.ai">support@snap-chart.ai</a>
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-3">{title}</h2>
      <div className="text-gray-300 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_strong]:text-white [&_p]:mb-2">{children}</div>
    </div>
  );
}
